import { useState, useEffect } from 'react'
import { colors } from '@/constant/colors'
import MarkdownComponent from '../Markdown'
import { getAssetUrl } from '@/constant/assets'
import ExpandExplanation from './expandExplanation'
import { useChatStore } from '@/store/useChatStore'

interface DragAndDropQuestion {
  id: string
  question: string
  answer: string[]
  explanation: string
}

interface DragAndDropData {
  questions: DragAndDropQuestion[]
}

interface DroppedWord {
  word: string
  questionId: string
  blankIndex: number
  isCorrect?: boolean
}

interface DragAndDropQuestionProps {
  question: any;
  handleAnswer: (option: string, isCorrect: boolean, autoSubmit?: boolean) => void;
  onInputChange?: (value: string) => void;
  currentAnswer: any;
  disabled: boolean;
  isReview?: boolean;
  submitted: boolean;
  bgColor?: string;
}

const DragAndDropQuestion = ({
  question,
  handleAnswer,
  currentAnswer,
  disabled,
  isReview = false,
  bgColor = colors.mainBgColor,
  submitted,
  onInputChange,
}: DragAndDropQuestionProps) => {
  const [wordBank, setWordBank] = useState<string[]>([])
  const [droppedWords, setDroppedWords] = useState<DroppedWord[]>([])
  const [usedWordIndices, setUsedWordIndices] = useState<Set<number>>(new Set())

  const [isCompleted, setIsCompleted] = useState(false)
  const [draggedWord, setDraggedWord] = useState<string | null>(null)
  const [dragOverTarget, setDragOverTarget] = useState<{ questionId: string; blankIndex: number } | null>(null)
  const [touchPosition, setTouchPosition] = useState<{ x: number; y: number } | null>(null)

  // Use the question prop directly instead of loading fake data
  const data: DragAndDropData | null = question ? {
    questions: question.questions || []
  } : null

  useEffect(() => {
    if (question && question.questions) {
      // Generate word bank from all answers
      const allWords: string[] = []
      question.questions.forEach((q: DragAndDropQuestion) => {
        if (q.answer && Array.isArray(q.answer)) {
          q.answer.forEach((word: string) => {
            allWords.push(word)
          })
        }
      })

      // Sort alphabetically and set word bank
      const sortedWords = Array.from(allWords).sort()
      setWordBank(sortedWords)
    }
  }, [question, currentAnswer])

  useEffect(() => {
    if (isReview) {
      let parsed = [];
      try {
        parsed = JSON.parse(currentAnswer.firstSelectedOption || '[]');
      } catch (e) {
        console.error('JSON parse error:', e, currentAnswer.firstSelectedOption);
      }
      setDroppedWords(parsed);
    } else if (currentAnswer.selectedOption === null) {
      handleReset();
    }
  }, [isReview, currentAnswer]);

  useEffect(() => {
    if (isReview) {
      return;
    }
    if (submitted) {
      if (!data) return

      // Check if all blanks are filled and correct
      const totalBlanks = data.questions.reduce((sum, q) => sum + q.answer.length, 0)
      const allBlanksCorrect = data.questions.every(question =>
        question.answer.every((_, index) => {
          const droppedWord = getWordInBlank(question.id, index)
          return droppedWord && question.answer[index] === droppedWord
        })
      )

      const userAnswersString = JSON.stringify(droppedWords)
      handleAnswer(userAnswersString, allBlanksCorrect, true)  // Pass autoSubmit=true
      // Audio feedback removed for batch submission

      if (allBlanksCorrect && droppedWords.length === totalBlanks) {
        setIsCompleted(true)
      }
    } else {
      handleReset()
    }
  }, [submitted]);

  const handleDragStart = (e: React.DragEvent, word: string, index: number) => {
    setDraggedWord(word)
    e.dataTransfer.setData('text/plain', index.toString())
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleTouchStart = (e: React.TouchEvent, word: string, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    console.log('Touch start - word:', word, 'index:', index)
    setDraggedWord(word)
    // 设置触摸位置
    if (e.touches[0]) {
      setTouchPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY })
      e.currentTarget.setAttribute('data-touch-index', index.toString())
    }
  }

  const handleDragEnd = () => {
    setDraggedWord(null)
    setDragOverTarget(null)
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault()
    e.stopPropagation()

    console.log('Touch end - draggedWord:', draggedWord, 'touchPosition:', touchPosition)

    if (draggedWord && touchPosition) {
      // 找到触摸结束位置下的目标元素
      const elementBelow = document.elementFromPoint(touchPosition.x, touchPosition.y)
      console.log('Element below touch:', elementBelow)

      if (elementBelow) {
        // 查找最近的空白框元素
        const blankElement = elementBelow.closest('[data-blank]')
        console.log('Blank element found:', blankElement)

        if (blankElement) {
          const questionId = blankElement.getAttribute('data-question-id')
          const blankIndex = parseInt(blankElement.getAttribute('data-blank-index') || '0')
          console.log('Question ID:', questionId, 'Blank Index:', blankIndex)

          if (questionId && !submitted && data) {
            console.log('Executing drop logic')
            // 执行放置逻辑
            const newDroppedWords = droppedWords.filter(
              item => !(item.questionId === questionId && item.blankIndex === blankIndex)
            )

            newDroppedWords.push({
              word: draggedWord,
              questionId,
              blankIndex,
              isCorrect: data.questions.find(q => q.id === questionId)?.answer[blankIndex] === draggedWord
            })

            setDroppedWords(newDroppedWords)
            refreshUsedWordIndices(newDroppedWords)

            // 调用 onInputChange 通知父组件有输入变化
            if (onInputChange) {
              onInputChange(JSON.stringify(newDroppedWords))
            }
          }
        }
      }
    }

    setDraggedWord(null)
    setDragOverTarget(null)
    setTouchPosition(null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  const handleDragEnter = (e: React.DragEvent, questionId: string, blankIndex: number) => {
    e.preventDefault()
    setDragOverTarget({ questionId, blankIndex })
  }

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragOverTarget(null)
    }
  }

  const handleDrop = (e: React.DragEvent, questionId: string, blankIndex: number) => {
    e.preventDefault()
    setDragOverTarget(null)

    if (draggedWord && !submitted && data) {
      // Remove any existing word in this blank
      const newDroppedWords = droppedWords.filter(
        item => !(item.questionId === questionId && item.blankIndex === blankIndex)
      )

      // Add the new word without correctness info (will be checked on submit)
      newDroppedWords.push({
        word: draggedWord,
        questionId,
        blankIndex,
        isCorrect: data.questions.find(q => q.id === questionId)?.answer[blankIndex] === draggedWord
      })

      setDroppedWords(newDroppedWords)

      // Refresh the used word indices based on current dropped words
      refreshUsedWordIndices(newDroppedWords)

      // 调用 onInputChange 通知父组件有输入变化
      if (onInputChange) {
        onInputChange(JSON.stringify(newDroppedWords))
      }
    }
  }



  const handleRemoveWord = (questionId: string, blankIndex: number) => {
    if (!isCompleted && !disabled) {
      const newDroppedWords = droppedWords.filter(
        item => !(item.questionId === questionId && item.blankIndex === blankIndex)
      );

      setDroppedWords(newDroppedWords);

      // Refresh the used word indices based on current dropped words
      refreshUsedWordIndices(newDroppedWords);

      // 调用 onInputChange 通知父组件有输入变化
      if (onInputChange) {
        onInputChange(JSON.stringify(newDroppedWords))
      }
    }
  }

  const getWordInBlank = (questionId: string, blankIndex: number) => {
    return droppedWords.find(
      item => item.questionId === questionId && item.blankIndex === blankIndex
    )?.word || null
  }

  const getBlankCorrectness = (questionId: string, blankIndex: number) => {
    return droppedWords.find(
      item => item.questionId === questionId && item.blankIndex === blankIndex
    )?.isCorrect || false
  }

  const handleReset = () => {
    // 保留做对的 blank
    const correctDroppedWords = droppedWords.filter(word => word.isCorrect);
    setDroppedWords(correctDroppedWords);
    setIsCompleted(false);

    // Refresh the used word indices based on current dropped words
    refreshUsedWordIndices(correctDroppedWords);

    // 调用 onInputChange 通知父组件有输入变化
    if (onInputChange) {
      onInputChange(JSON.stringify(correctDroppedWords))
    }
  }

  const refreshUsedWordIndices = (currentDroppedWords: DroppedWord[]) => {
    const newUsedIndices = new Set<number>();

    // 为每个已使用的单词找到对应的 wordBank 索引
    currentDroppedWords.forEach(droppedWord => {
      const wordIndex = wordBank.findIndex((word, index) =>
        word === droppedWord.word && !newUsedIndices.has(index)
      );
      if (wordIndex >= 0) {
        newUsedIndices.add(wordIndex);
      }
    });

    setUsedWordIndices(newUsedIndices);
  }

  const getCorrectCount = () => {
    if (!data) return 0
    let correct = 0
    data.questions.forEach(question => {
      question.answer.forEach((answer, index) => {
        if (getWordInBlank(question.id, index) === answer) {
          correct++
        }
      })
    })
    return correct
  }

  const getTotalBlanks = () => {
    if (!data) return 0
    return data.questions.reduce((sum, q) => sum + q.answer.length, 0)
  }

  const renderQuestionWithBlanks = (question: DragAndDropQuestion) => {
    const parts = question.question.split('[BLANK]')

    return (
      <div style={{ fontSize: '18px', lineHeight: '2', color: colors.mainTextColor }}>
        {parts.map((part: string, index: number) => (
          <span key={index}>
            <span dangerouslySetInnerHTML={{ __html: part }} />
            {index < parts.length - 1 && (
              <span
                data-blank="true"
                data-question-id={question.id}
                data-blank-index={index}
                className={`inline-flex items-center justify-center min-w-[100px] h-8 mx-1 px-2 border-2 border-dashed rounded transition-all align-middle relative
                  before:absolute before:left-[-40px] before:right-[-40px] before:top-[-20px] before:bottom-[-20px] before:z-0 
                  ${getWordInBlank(question.id, index)
                    ? submitted
                      ? getBlankCorrectness(question.id, index)
                        ? 'border-green-400 bg-green-50'
                        : 'border-red-400 bg-red-50'
                      : 'border-blue-300 bg-blue-50'
                    : dragOverTarget?.questionId === question.id && dragOverTarget?.blankIndex === index
                      ? 'border-blue-500 bg-blue-50'
                      : submitted && !getWordInBlank(question.id, index)
                        ? 'border-red-400 bg-red-50'
                        : 'border-gray-400 bg-white'
                    } ${!submitted ? 'cursor-pointer' : ''}`}
                style={{ verticalAlign: 'baseline', touchAction: 'none' }}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnter(e, question.id, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, question.id, index)}
                onTouchMove={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (draggedWord && e.touches[0]) {
                    setTouchPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY })
                  }
                  return false
                }}
                onTouchEnd={(e) => {
                  // Touch drop is now handled in the global handleTouchEnd
                }}
              >
                {getWordInBlank(question.id, index) ? (
                  <span
                    className={`text-sm font-medium px-2 py-1 rounded select-none ${submitted
                      ? getBlankCorrectness(question.id, index)
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700 line-through'
                      : 'bg-blue-100 text-blue-700'
                      } ${!submitted ? 'cursor-move' : ''}`}
                    onClick={() => !submitted && handleRemoveWord(question.id, index)}
                  >
                    {getWordInBlank(question.id, index)}
                    {!submitted && (
                      <span className="ml-1 text-xs opacity-60">×</span>
                    )}
                  </span>
                ) : (
                  <span className="text-gray-400 text-sm">
                    {submitted ? (
                      <span className="text-red-600">
                        {question.answer[index]}
                      </span>
                    ) : (
                      'Drop here'
                    )}
                  </span>
                )}
              </span>
            )}
          </span>
        ))}
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center min-h-[400px]" style={{ backgroundColor: colors.mainBgColor }}>
        <div className="text-xl emphasize-text" style={{ color: colors.loadingColor }}>Loading...</div>
      </div>
    )
  }

  return (
    <div className="w-full relative">
      {/* Touch drag visual feedback */}
      {draggedWord && touchPosition && (
        <div
          className="fixed z-50 pointer-events-none bg-blue-100 text-blue-700 px-3 py-2 rounded-lg border-2 border-blue-300 shadow-lg"
          style={{
            left: touchPosition.x,
            top: touchPosition.y,
            transform: 'translate(-50%, -50%)'
          }}
        >
          {draggedWord}
        </div>
      )}


      {/* Sticky Word Bank */}
      {!isReview && <div className="sticky top-25 z-10 border-b-2 border-gray-200" style={{ backgroundColor: bgColor }}>
        <div className="max-w-4xl mx-auto px-8 py-4">

          <div className="flex flex-wrap gap-2">
            {wordBank.map((word, index) => {
              // 检查这个具体的单词实例是否已被使用
              const isUsed = usedWordIndices.has(index);

              return (
                <div
                  key={`${word}-${index}`}
                  draggable={!submitted && !isUsed}
                  onDragStart={(e) => !isUsed && handleDragStart(e, word, index)}
                  onDragEnd={handleDragEnd}
                  onTouchStart={(e) => !isUsed && handleTouchStart(e, word, index)}
                  onTouchEnd={handleTouchEnd}
                  onTouchMove={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (draggedWord && e.touches[0]) {
                      setTouchPosition({ x: e.touches[0].clientX, y: e.touches[0].clientY })
                    }
                    return false
                  }}
                  style={{ touchAction: 'none' }}
                  className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all select-none ${isUsed
                    ? 'bg-gray-100 text-gray-600 border-gray-300 cursor-not-allowed opacity-50'
                    : submitted
                      ? 'bg-gray-100 text-gray-700 border-gray-300 cursor-not-allowed'
                      : 'bg-blue-50 text-blue-700 border-blue-300 cursor-move hover:bg-blue-100 hover:border-blue-400'
                    } ${draggedWord === word ? 'opacity-50' : ''}`}
                >
                  {word}
                </div>
              );
            })}
          </div>
        </div>
      </div>}

      <div className="max-w-4xl mx-auto px-8 pb-6 pt-0">

        {/* Description */}
        <div className="mb-6">
          <MarkdownComponent
            content={question.description}
            textStyle={{ color: colors.mainTextColor }}
          />
        </div>

        {/* All Questions */}
        <div className="space-y-6">
          {data?.questions?.map((question: any, index: number) => {
            // Check if all blanks in this question are filled and correct (only after submission)
            const rowBlanksCorrect = submitted && question.answer.every((_: any, i: number) =>
              getWordInBlank(question.id, i) && getBlankCorrectness(question.id, i)
            )

            return (
              <div key={question.id} className="border-b border-gray-200 last:border-b-0">
                {/* Question */}
                <div className="mb-2">
                  <div className="flex gap-4 items-start">
                    <span className="text-lg emphasize-text flex-shrink-0" style={{ color: colors.secondaryColor, lineHeight: '2' }}>
                      {String.fromCharCode(97 + index)}.
                    </span>
                    <div className="flex-1">
                      {renderQuestionWithBlanks(question)}
                    </div>
                  </div>
                </div>

                {/* Explanation - show after submission */}
                {submitted && (
                  <ExpandExplanation explanation={question.explanation} initialHideExplanation={rowBlanksCorrect} />
                )}
              </div>
            )
          })}
        </div>


        {/* Score Display */}
        {!isReview && submitted && (
          <div className="flex justify-center mt-8">
            <div className="text-center">
              <div className="text-4xl emphasize-text mb-2" style={{
                color: getCorrectCount() === getTotalBlanks()
                  ? colors.correctAnswerColor
                  : colors.mainTextColor
              }}>
                {getCorrectCount()} / {getTotalBlanks()}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

export default DragAndDropQuestion